import { useEffect, useRef } from "react";
import { useAuth } from "@/lib/auth/AuthContext";
import { useUser } from "@/lib/query/hooks/useUser";
import { useTheme } from "@/lib/theme/ThemeProvider";
import type { Theme } from "@/lib/theme/themeStore";

// Reconciles the locally cached theme (which the index.html pre-paint
// script already used for first paint) with the authoritative value
// stored on the User. Mounted only when authenticated so the unauthed
// login screen never fires `useUser` and never overrides the default
// "light" pre-paint.
//
// On the login lane `hydrateAccountPresentation` has normally already applied
// the account's theme BEFORE the dashboard mounts, so this is the safety net
// for cold boots (valid cookie, no login form) and for a theme changed on
// another device.
function ThemeSyncInner() {
  const { data: user } = useUser();
  const { theme, setTheme } = useTheme();
  const remote = user?.theme;
  // Push each DB value ONCE. Reacting to every drift instead turns two tabs
  // holding different accounts into a write-back loop over the `storage`
  // event — each tab correcting the other, forever (visible as flicker).
  // A local or cross-tab choice made after this point is the user's, and wins.
  const applied = useRef<Theme | null>(null);

  useEffect(() => {
    if (remote !== "light" && remote !== "dark") return;
    if (applied.current === remote) return;
    applied.current = remote;
    if (remote !== theme) setTheme(remote);
  }, [remote, theme, setTheme]);

  return null;
}

export function ThemeSync() {
  const { state } = useAuth();
  if (state.kind !== "authenticated") return null;
  return <ThemeSyncInner />;
}
